import { useState, useMemo } from 'react'
import { useMutation } from '@urql/next'
import { UpdateShowcaseItemsDocument } from '../../(gql)/update-showcase.graphql'
import { CreateShowcaseDocument } from '../../(gql)/create-showcase.graphql'
import type { ShowcaseFragmentFragment } from '@/gql/generated/graphql'

type UseShowcaseEditorProps = {
  profileId: number
  showcase?: ShowcaseFragmentFragment
  showcaseTokens: Array<{ id: string }>
  userTokens: Array<{ id: string }>
  onSaveSuccess: () => void
}

export type UseShowcaseEditorReturn = {
  isEditMode: boolean
  isSaving: boolean
  selectedTokenIds: string[]
  visibleTokenCount: number
  handleEditShowcase: () => void
  handleCreateShowcase: () => void
  handleToggleToken: (tokenId: string) => void
  handleDeselectAll: () => void
  handleSaveShowcase: () => Promise<void>
  handleShowMore: () => void
}

const TOKENS_PER_PAGE = 100

export const useShowcaseEditor = ({
  profileId,
  showcase,
  showcaseTokens,
  userTokens,
  onSaveSuccess
}: UseShowcaseEditorProps): UseShowcaseEditorReturn => {
  const [isEditMode, setIsEditMode] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedTokenIds, setSelectedTokenIds] = useState<string[]>([])
  const [visibleTokenCount, setVisibleTokenCount] = useState(TOKENS_PER_PAGE)

  // Initialize selected tokens with current showcase tokens (preserving order)
  const initialSelectedTokenIds = useMemo(() => {
    return showcaseTokens.map((token) => token.id)
  }, [showcaseTokens])

  // Mutations for showcase operations
  const [, updateShowcase] = useMutation(UpdateShowcaseItemsDocument)
  const [, createShowcase] = useMutation(CreateShowcaseDocument)

  const handleEditShowcase = () => {
    setSelectedTokenIds(initialSelectedTokenIds)
    setVisibleTokenCount(TOKENS_PER_PAGE)
    setIsEditMode(true)
  }

  const handleCreateShowcase = () => {
    setSelectedTokenIds([])
    setVisibleTokenCount(TOKENS_PER_PAGE)
    setIsEditMode(true)
  }

  const handleToggleToken = (tokenId: string) => {
    setSelectedTokenIds((prev) => {
      if (prev.includes(tokenId)) {
        // Remove token (preserve order of remaining tokens)
        return prev.filter((id) => id !== tokenId)
      } else {
        // Add token to the end
        return [...prev, tokenId]
      }
    })
  }

  const handleDeselectAll = () => {
    setSelectedTokenIds([])
  }

  const handleShowMore = () => {
    setVisibleTokenCount((prev) => Math.min(prev + TOKENS_PER_PAGE, userTokens.length))
  }

  const handleSaveShowcase = async () => {
    setIsSaving(true)

    try {
      if (showcase) {
        // Case 1: Update existing showcase
        const showcaseItems = selectedTokenIds.map((tokenId, index) => ({
          showcase_id: showcase.id,
          token_id: tokenId,
          display_order: index
        }))

        const result = await updateShowcase({
          showcaseId: showcase.id,
          showcaseItems
        })

        if (result.error) {
          console.error('Error updating showcase:', result.error)
          setIsSaving(false)
          return
        }
      } else {
        // Case 2: Create new showcase with nested items
        const showcaseInput = {
          user_profile_id: profileId,
          showcase_items: {
            data: selectedTokenIds.map((tokenId, index) => ({
              token_id: tokenId,
              display_order: index
            }))
          }
        }

        const result = await createShowcase({
          showcase: showcaseInput
        })

        if (result.error) {
          console.error('Error creating showcase:', result.error)
          return
        }
      }

      setIsEditMode(false)
      onSaveSuccess()
    } catch (error) {
      console.error('Error saving showcase:', error)
    } finally {
      setIsSaving(false)
    }
  }

  return {
    // State
    isEditMode,
    isSaving,
    selectedTokenIds,
    visibleTokenCount,

    // Handlers
    handleEditShowcase,
    handleCreateShowcase,
    handleToggleToken,
    handleDeselectAll,
    handleSaveShowcase,
    handleShowMore
  }
}
